import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { Link, useLocation, useNavigate } from "react-router-dom";

import Sidebar from "../sidebar";
import { deleteCustomer, getCustomers } from "./customers-section.service";
import Swal from "sweetalert2";

export default function CustomersSection() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pageParam = queryParams.get("page");
  const searchParam = queryParams.get("search");
  const [currentPage, setCurrentPage] = useState(
    pageParam ? parseInt(pageParam) : 1
  );

  const [search, setSearch] = useState(searchParam || "");
  const queryClient = useQueryClient();

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(parseInt(pageParam));
    }
  }, [pageParam]);

  const {
    isPending,
    isError,
    data: response,
    error,
  } = useQuery({
    queryKey: ["admin-customers", currentPage, search],
    queryFn: () => getCustomers({ page: currentPage, search }),
    placeholderData: keepPreviousData,
  });

  const handlePageChange = (selectedItem: { selected: number }) => {
    const newPage = selectedItem.selected + 1;
    setCurrentPage(newPage);
    navigate(`?page=${newPage}`);
  };

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      await deleteCustomer(id);
      queryClient.invalidateQueries({ queryKey: ["admin-customers"] });
      Swal.fire("Deleted!", "The customer has been deleted.", "success");
    }
  };

  return (
    <section className="ec-page-content ec-vendor-dashboard section-space-p">
      <div className="container">
        <div className="row">
          <Sidebar />
          <div className="ec-shop-rightside col-lg-9 col-md-12">
            <div className="ec-vendor-dashboard-card space-bottom-30">
              <div className="ec-vendor-card-header">
                <h5>Customers</h5>
                <div className="ec-header-btn">
                  <Link className="btn btn-lg btn-primary" to="add">
                    Add Customer
                  </Link>
                </div>
              </div>
              <div className="ec-vendor-card-header p-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search customers..."
                  onChange={(e) => {
                    const searchQuery = e.target.value;
                    navigate(`?page=1&search=${searchQuery}`);
                    setSearch(searchQuery);
                  }}
                />
              </div>
              <div className="ec-vendor-card-body">
                <div className="ec-vendor-card-table">
                  <table className="table ec-table">
                    <thead>
                      <tr>
                        <th scope="col">Company Name</th>
                        <th scope="col">Country</th>
                        <th scope="col">Contact Person</th>
                        <th scope="col">Default Size</th>
                        <th scope="col">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {response?.data.results.map((customer: any) => (
                        <tr key={customer.id}>
                          <td>
                            <span>{customer.name}</span>
                          </td>
                          <td>
                            <span>{customer.country.name}</span>
                          </td>
                          <td>
                            <span>{customer.contactPerson}</span>
                          </td>
                          <td>
                            <span>{customer.defaultSize}</span>
                          </td>
                          <td>
                            <span>
                              <Link
                                to={`edit/${customer.id}`}
                                className="ec-action-btn mr-1"
                              >
                                <i className="fa fa-edit fs-5"></i>
                              </Link>
                              <Link
                                onClick={() => {
                                  handleDelete(customer.id);
                                }}
                                to={""}
                                className="ec-action-btn text-danger "
                              >
                                <i className="fa fa-trash fs-5"></i>
                              </Link>
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="col-lg-12">
                  <ReactPaginate
                    nextLabel=">>"
                    onPageChange={handlePageChange}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={2}
                    pageCount={response?.data.totalPages}
                    forcePage={currentPage - 1}
                    previousLabel="<<"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    previousClassName="page-item"
                    previousLinkClassName="page-link"
                    nextClassName="page-item"
                    nextLinkClassName="page-link"
                    breakLabel="..."
                    breakClassName="page-item"
                    breakLinkClassName="page-link"
                    containerClassName="pagination"
                    activeClassName="active"
                    renderOnZeroPageCount={null}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
